import React, { useContext, useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TablePagination,
    TableRow,
    TableCell,
    Typography,
    Avatar,
    Stack,
    Box,
    Chip,
} from '@mui/material';
import MerchantWarehouseTableHead from './MerchantWarehouseTableHead';
import { MerchantWarehouseContext } from '../../context';
import moment from 'moment/moment';
import { Cancel, CheckCircle, EditNoteOutlined, Person2, Email, Phone } from '@mui/icons-material';
import TableSkeleton from '../common/TableSkeleton';

export default function MerchantWarehouseTable() {
    const { firstLoading, allData, allAssignments, page, setPage, rowsPerPage, setRowsPerPage, viewDetail, toggleStatus } = useContext(MerchantWarehouseContext);

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('created_at');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target.value);
        setPage(0);
    };

    const sortedRows = useMemo(() => {
        return [...allAssignments].sort((a, b) => {
            let valA = a[orderBy];
            let valB = b[orderBy];
            if (orderBy === 'merchant') {
                valA = a.merchant?.company_name || '';
                valB = b.merchant?.company_name || '';
            }
            if (order === 'asc') {
                return valA > valB ? 1 : -1;
            } else {
                return valA < valB ? 1 : -1;
            }
        });
    }, [allAssignments, orderBy, order]);

    const stickyActionStyle = {
        position: 'sticky',
        right: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #ddd',
        boxShadow: 'inset 4px 0 8px -4px rgba(0, 0, 0, 0.2)',
        zIndex: 3,
    };

    return (
        <div className='table-component'>
            {firstLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={allData?.TotalRows || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : null}
            <TableContainer>
                <Table size="small">
                    <MerchantWarehouseTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={sortedRows.length}
                    />
                    <TableBody>
                        {!firstLoading ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center" sx={{ p: 0 }}>
                                    <TableSkeleton rows={10} columns={9} />
                                </TableCell>
                            </TableRow>
                        ) : sortedRows.length > 0 ?
                            sortedRows.map((row) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={row.id}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row.id}
                                        </TableCell>
                                        <TableCell align="left">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#004d40' }}>
                                                    <Person2 />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {row.merchant?.company_name || 'NA'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {row.merchant?.brand_name}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Stack spacing={0.5}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    <Typography variant="caption">{row.merchant?.email || 'NA'}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    <Typography variant="caption">{row.merchant?.mobile || 'NA'}</Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="left">{row.start_date ? moment(row.start_date).format('DD-MM-YYYY') : 'NA'}</TableCell>
                                        <TableCell align="right">{row.area_per_sqft || 0}</TableCell>
                                        <TableCell align="right">{row.price_per_sqft || 0}</TableCell>
                                        <TableCell align="left">
                                            <Chip
                                                variant="outlined"
                                                label={row?.status === 1 ? 'Active' : 'InActive'}
                                                color={row?.status === 1 ? 'success' : 'error'}
                                                size="small"
                                                icon={row?.status === 1 ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                                                onClick={() => toggleStatus(row.id, row?.status === 1 ? 0 : 1)}
                                            />
                                        </TableCell>
                                        <TableCell align="left" sx={stickyActionStyle}>
                                            <Stack direction="row" spacing={1}>
                                                <Chip
                                                    variant="outlined"
                                                    label={'Edit'}
                                                    color={'default'}
                                                    size="small"
                                                    icon={<EditNoteOutlined fontSize="small" />}
                                                    onClick={() => viewDetail(row.id)}
                                                />
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        No data found
                                    </TableCell>
                                </TableRow>
                            )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
